import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink as OpenIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentFunctionRequestDetailDto } from '../types/agentFunction';
import { MessageRole } from '../types/session';
import { ROUTES } from '../utils/routes';

const getStatusChip = (status: number) => {
  const map: Record<number, { label: string; color: 'default' | 'amber' | 'green' | 'red' }> = {
    0: { label: 'Pending', color: 'default' },
    1: { label: 'Processing', color: 'amber' },
    2: { label: 'Completed', color: 'green' },
    3: { label: 'Failed', color: 'red' },
  };
  const cfg = map[status] ?? { label: 'Unknown', color: 'default' };
  return <Chip color={cfg.color} label={cfg.label} />;
};

const getRoleLabel = (role: number) => {
  const map: Record<number, string> = {
    [MessageRole.System]: 'System',
    [MessageRole.User]: 'User',
    [MessageRole.Assistant]: 'Assistant',
    [MessageRole.Tool]: 'Tool',
  };
  return map[role] ?? 'Unknown';
};

const formatOutputJson = (json: string | undefined): string => {
  if (!json || !json.trim()) return '{}';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

export const AgentFunctionRequestDetailPage = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [data, setData] = useState<AgentFunctionRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const loadData = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await agentFunctionService.getAgentFunctionRequest(requestId);
      setData(result);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load request details';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [requestId, addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="agent-function-request-detail" data-testid="agent-function-request-detail-page">
        <ShellHeader title="Execution Request" description="Loading…" />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid="agent-function-request-detail-loading" />
        </PageContainer>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="agent-function-request-detail" data-testid="agent-function-request-detail-page">
        <ShellHeader
          title="Execution Request"
          description="Request not found."
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="back-agent-function-request-btn">
              Back
            </Button>
          }
        />
        <PageContainer>
          <InlineErrorPanel message={error || 'Request not found'} data-testid="agent-function-request-detail-error" />
        </PageContainer>
      </div>
    );
  }

  const session = data.session;
  const messages = data.messages ?? [];
  const files = data.files ?? [];

  return (
    <div className="agent-function-request-detail" data-testid="agent-function-request-detail-page">
      <ShellHeader
        title="Execution Request"
        description={`${data.agentFunctionName} · ${data.agentFunctionSlug}`}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                data.agentFunctionId
                  ? `/agent-functions/${data.agentFunctionId}/requests`
                  : '/agent-functions'
              )
            }
            data-testid="back-agent-function-request-btn"
          >
            Back
          </Button>
        }
      />

      <PageContainer>
      {/* Tabs */}
      <div className="page-detail-layout__tabs" role="tablist" aria-label="Request sections">
        {[
          { label: 'Request & Response', value: 0, testId: 'agent-function-request-tab-request-response' },
          { label: 'Session Data', value: 1, testId: 'agent-function-request-tab-session' },
          { label: 'Files', value: 2, testId: 'agent-function-request-tab-files' },
        ].map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={tabIndex === tab.value}
            className="page-detail-layout__tab"
            onClick={() => setTabIndex(tab.value)}
            data-testid={tab.testId}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab 0: Request & Response */}
      {tabIndex === 0 && (
        <div className="flex flex-col gap-4">
          {/* Header: Status, Duration, Started At, Completed At */}
          <div className="flex flex-wrap gap-3 items-center">
            {getStatusChip(data.status)}
            <span className="text-sm text-[var(--color-text-secondary)]">
              Duration: {data.durationMs != null ? `${data.durationMs} ms` : '—'}
            </span>
            <span className="text-sm text-[var(--color-text-secondary)]">
              Started: {data.startedAt ? new Date(data.startedAt).toLocaleString() : '—'}
            </span>
            <span className="text-sm text-[var(--color-text-secondary)]">
              Completed: {data.completedAt ? new Date(data.completedAt).toLocaleString() : '—'}
            </span>
          </div>

          {/* Breadcrumb: Function Name, Slug */}
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-sm font-semibold">
              {data.agentFunctionName}
            </span>
            <span className="text-sm text-[var(--color-text-secondary)]">
              {data.agentFunctionSlug}
            </span>
          </div>

          <hr className="border-[var(--color-border-default)]" />

          {/* Error message if failed */}
          {data.status === 3 && data.errorMessage && (
            <InlineErrorPanel message={data.errorMessage} data-testid="agent-function-request-detail-error" />
          )}

          {/* Input JSON */}
          <div>
            <h6 className="text-base font-semibold mb-1">
              Input
            </h6>
            <CodeEditor
              value={data.inputJson || '{}'}
              language="json"
              height="200px"
              readOnly
            />
          </div>

          {/* Output JSON */}
          <div>
            <h6 className="text-base font-semibold mb-1">
              Output
            </h6>
            {data.outputJson ? (
              <CodeEditor
                value={formatOutputJson(data.outputJson)}
                language="json"
                height="200px"
                readOnly
              />
            ) : (
              <>
                <CodeEditor value="{}" language="json" height="100px" readOnly />
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  No output yet
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab 1: Session Data */}
      {tabIndex === 1 && (
        <div className="flex flex-col gap-4">
          {session ? (
            <>
              {/* Session summary */}
              <FormSection title={session.sessionName || 'Session'}>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Chip
                      color={
                        session.status === 2
                          ? 'green'
                          : session.status === 5
                            ? 'red'
                            : 'default'
                      }
                      label={['Active', 'Paused', 'Completed', 'Cancelled', 'Expired', 'Error'][
                        session.status
                      ] ?? 'Unknown'}
                    />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      Messages: {session.messageCount}
                    </span>
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      Tokens: {session.totalTokensUsed ?? 0}
                    </span>
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      Cost: ${(session.totalCost ?? 0).toFixed(4)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() =>
                        navigate(ROUTES.SESSION_DETAIL.replace(':id', session.id))
                      }
                    >
                      <OpenIcon className="h-4 w-4" />
                      Open full session
                    </Button>
                  </div>
              </FormSection>

              {/* Message list */}
              <h6 className="text-base font-semibold">
                Messages
              </h6>
              <div className="flex flex-col gap-2">
                {messages.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    No messages
                  </p>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className="p-2 rounded border border-[var(--color-border-default)]"
                    >
                        <div className="flex justify-between items-center mb-1">
                          <Chip variant="outlined" label={getRoleLabel(msg.role)} />
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            {msg.createdAt
                              ? new Date(msg.createdAt).toLocaleString()
                              : ''}
                          </span>
                        </div>
                        <pre className="text-sm whitespace-pre-wrap break-words font-mono">
                          {msg.content || '(empty)'}
                        </pre>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              No session data available for this request.
            </p>
          )}
        </div>
      )}

      {/* Tab 2: Files */}
      {tabIndex === 2 && (
        <div>
          {files.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)]">
              No files attached
            </p>
          ) : (
            <div className="rounded-xl border border-[var(--color-border-default)] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border-default)] bg-[var(--color-bg-inset)]">
                    <th className="px-3 py-2 text-left font-semibold">File Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Content Type</th>
                    <th className="px-3 py-2 text-right font-semibold">Size</th>
                    <th className="px-3 py-2 text-left font-semibold">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.id} className="border-b border-[var(--color-border-default)] last:border-b-0">
                      <td className="px-3 py-2">{f.originalFileName || f.fileName}</td>
                      <td className="px-3 py-2">{f.contentType}</td>
                      <td className="px-3 py-2 text-right">{f.fileSizeBytes}</td>
                      <td className="px-3 py-2">
                        {f.createdAt
                          ? new Date(f.createdAt).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </PageContainer>
    </div>
  );
};
